import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useFormHook } from '@Hooks/index';
import { EddEmbeddedFields } from '@Pages/happyFlowPages/eddQuestions/pages/embedded/fields.types';
import {
  getDefaultValueByQuestionId,
  saveEddQuestionToLocalStore,
} from '@Pages/happyFlowPages/eddQuestions/shared.utils';
import { EDD_VERSION, EddYesNo } from '@Pages/happyFlowPages/eddQuestions/consts';
import { useBoundStore } from '@Store/useBoundStore';
import { useNavigationManager } from '@Router/useNavigationManager';
import { usePostEddData } from '@Pages/happyFlowPages/eddQuestions/hooks/usePostEddData';
import { RoutesMap } from '@Router/router.types';
import { reportError, ObservabilityErrorSeverity } from '@Services/observability/initObservability';

export const useTaxLegalServicesLogic = () => {
  const { mutateAsync: postEddData, isPending: isPostingEddData } = usePostEddData();
  const { getEddEmbeddedQuestions } = useBoundStore((state) => state);
  const { navigateToNextPage } = useNavigationManager();
  const { t } = useTranslation('eddQuestions');
  const { t: tValidation } = useTranslation('enJSON');

  const formMethods = useFormHook({
    schemaTitle: 'tax-legal-services',
    formDefaultValues: {
      [EddEmbeddedFields.EDD_TAX_LEGAL_SERVICES]:
        getDefaultValueByQuestionId(EddEmbeddedFields.EDD_TAX_LEGAL_SERVICES) || EddYesNo.NO,
      [EddEmbeddedFields.EDD_TAX_LEGAL_SERVICES_US]:
        getDefaultValueByQuestionId(EddEmbeddedFields.EDD_TAX_LEGAL_SERVICES_US) || EddYesNo.NO,
    },
    formSchema: z
      .object({
        [EddEmbeddedFields.EDD_TAX_LEGAL_SERVICES]: z
          .string()
          .min(1, { message: tValidation('validation.required') }),
        [EddEmbeddedFields.EDD_TAX_LEGAL_SERVICES_US]: z.string().optional(),
      })
      .superRefine((data, ctx) => {
        if (data[EddEmbeddedFields.EDD_TAX_LEGAL_SERVICES] === EddYesNo.YES) {
          const followUp = data[EddEmbeddedFields.EDD_TAX_LEGAL_SERVICES_US];
          if (!followUp || followUp.length === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: tValidation('validation.required'),
              path: [EddEmbeddedFields.EDD_TAX_LEGAL_SERVICES_US],
            });
          }
        }
      }),
  });

  const handleSubmit = async () => {
    const mainValue = formMethods.getValues(EddEmbeddedFields.EDD_TAX_LEGAL_SERVICES);
    const followUpValue = formMethods.getValues(EddEmbeddedFields.EDD_TAX_LEGAL_SERVICES_US);

    saveEddQuestionToLocalStore({
      question: t('organic.taxLegalServices.title'),
      questionId: EddEmbeddedFields.EDD_TAX_LEGAL_SERVICES,
      answer: JSON.stringify(mainValue),
    });

    if (mainValue === EddYesNo.YES) {
      saveEddQuestionToLocalStore({
        question: t('organic.taxLegalServices.followUpTitle'),
        questionId: EddEmbeddedFields.EDD_TAX_LEGAL_SERVICES_US,
        answer: JSON.stringify(followUpValue),
      });
    }

    try {
      await postEddData({
        version: EDD_VERSION,
        results: getEddEmbeddedQuestions(),
      });
      navigateToNextPage(RoutesMap.EDD_TAX_LEGAL_SERVICES);
    } catch (error) {
      reportError({
        severity: ObservabilityErrorSeverity.ERROR,
        metadata: {
          name: 'EddTaxLegalServicesSubmitError',
          message: error instanceof Error ? error.message : String(error),
        },
        groupingHash: 'edd-submit-error',
      });
    }
  };

  return {
    formMethods,
    handleSubmit,
    isLoading: isPostingEddData,
  };
};
